import { useQuery } from "@/api/utils/useQuery"
import { QueryResult } from "@/api/utils/types"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import { DcaLitigationFileSummaryResponse } from "@/api/backofficeRest/generated/model"
import {
  IDcaLitigationFileSummary,
  IDcaLitigationRejectedCount,
} from "@/models/dcaAllocation/dcaLitigationFileSummary.types"

/**
 * Reads the summary of the most recently processed Moriarty litigation-placement-update
 * file, for the DCA Management page.
 *
 * This is a REST endpoint on be-api-gateway-backoffice (be-microservices SO-32859), read
 * via the orval-generated `backofficeRest` client.
 */

/** unknown -> number, tolerant of the endpoint returning counts as strings. */
const toCount = (value: unknown): number => Number(value ?? 0)

export const litigationFileSummaryFrom = (
  raw: DcaLitigationFileSummaryResponse,
): IDcaLitigationFileSummary => ({
  filename: raw.filename ?? null,
  processedAt: raw.processedAt ?? null,
  acceptedCount: toCount(raw.acceptedCount),
  rejectedCount: toCount(raw.rejectedCount),
  rejectedBreakdown: (raw.rejectedBreakdown ?? [])
    .flatMap((entry) => (entry ? [entry] : []))
    .map((entry): IDcaLitigationRejectedCount => ({
      dca: entry.dca ?? "",
      count: toCount(entry.count),
    })),
})

interface IDcaLitigationFileSummaryService {
  getLatestFileSummary: () => QueryResult<IDcaLitigationFileSummary>
}

class DcaLitigationFileSummaryServiceClass implements IDcaLitigationFileSummaryService {
  getLatestFileSummary(): QueryResult<IDcaLitigationFileSummary> {
    return useQuery<
      DcaLitigationFileSummaryResponse,
      IDcaLitigationFileSummary
    >({
      ...BackofficeRestQueries.dcaAllocation.litigationFileSummary,
      useRawResponse: true,
      placeholderData: {
        filename: null,
        processedAt: null,
        acceptedCount: 0,
        rejectedCount: 0,
        rejectedBreakdown: [],
      },
      parserFn: (raw) => litigationFileSummaryFrom(raw),
    })
  }
}

export const DcaLitigationFileSummaryService =
  new DcaLitigationFileSummaryServiceClass()
